import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Button } from "./ui/button";
import { Copy, Newspaper, Twitter, FileText, Check } from "lucide-react";
import { toast } from "sonner";
import { nearestCity, VOLCANO_ALERTS } from "../lib/api";

const fmtWIB = (t) =>
  new Date(t).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", dateStyle: "long", timeStyle: "short" }) + " WIB";
const fmtUTC = (t) => new Date(t).toISOString().replace("T", " ").slice(0, 16) + " UTC";

function depthLabel(d) {
  if (d < 50) return "dangkal (< 50 km)";
  if (d <= 300) return "menengah (50–300 km)";
  return "dalam (> 300 km)";
}

function tsunamiClause(q) {
  if (!q.tsunami) return "Berdasarkan parameter gempa, kejadian ini tidak berpotensi menimbulkan tsunami.";
  if (typeof q.tsunami === "string") return `Berdasarkan analisis awal, kejadian ini ${q.tsunami.toLowerCase()}.`;
  return "Berdasarkan parameter gempa, terdapat potensi tsunami. Warga di pesisir agar menjauh dari garis pantai dan mengikuti arahan BMKG.";
}

function buildQuake(q, len) {
  const near = nearestCity(q.latitude, q.longitude);
  const mag = q.magnitude?.toFixed(1);
  const depth = q.depth?.toFixed(0);
  const coord = `${q.latitude?.toFixed(2)}° , ${q.longitude?.toFixed(2)}°`;
  const src = q.source || "USGS";
  const nearStr = near ? `sekitar ${near.distanceKm.toFixed(0)} km dari ${near.name}` : "";

  if (len === "short") {
    return `⚠ GEMPA M ${mag} ${q.location}. Kedalaman ${depth} km. ${fmtWIB(q.time)}. Sumber: ${src}. ${q.tsunami ? "⚠ POTENSI TSUNAMI." : "Tidak berpotensi tsunami."} #PANTAU #Gempa`;
  }

  if (len === "detailed") {
    return `SIARAN PERS — GEMPA BUMI M ${mag}
${q.location.toUpperCase()}

Jakarta, ${fmtWIB(q.time)} — Sebuah gempa bumi berkekuatan magnitudo ${mag} mengguncang ${q.location} pada ${fmtWIB(q.time)} (${fmtUTC(q.time)}). Gempa berpusat pada koordinat ${coord} dengan kedalaman hiposenter ${depth} km, tergolong gempa ${depthLabel(q.depth)}${nearStr ? `, ${nearStr}` : ""}.

Data parameter dirilis oleh ${src}${src === "BMKG" ? " (Badan Meteorologi, Klimatologi, dan Geofisika)" : " (United States Geological Survey)"}. ${tsunamiClause(q)}
${q.felt ? `Gempa dilaporkan dirasakan di wilayah: ${q.felt}.` : ""}
${q.mmi ? `Intensitas maksimum (MMI): ${q.mmi}.` : ""}

IMBAUAN
Warga diimbau tetap tenang, tidak terpancing informasi tidak resmi, dan hanya merujuk pada sumber resmi BMKG serta BNPB. Periksa struktur bangunan sebelum kembali beraktivitas dan waspadai kemungkinan gempa susulan.

KONTAK DARURAT
BNPB 117 · BASARNAS 115 · Ambulans 118/119 · Polisi 110 · Damkar 113

SUMBER
${src} · Real-time feed PANTAU (${new Date().toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })} WIB)`;
  }

  // standard
  return `Gempa M ${mag} ${q.location}

Jakarta — Gempa bumi berkekuatan magnitudo ${mag} mengguncang ${q.location} pada ${fmtWIB(q.time)}. Episenter berada pada koordinat ${coord} dengan kedalaman ${depth} km${nearStr ? ` (${nearStr})` : ""}. Data dirilis oleh ${src}.

${tsunamiClause(q)}${q.felt ? ` Guncangan dilaporkan dirasakan di ${q.felt}.` : ""}

Warga diimbau tetap tenang dan mengikuti informasi resmi dari BMKG dan BNPB. Untuk kondisi darurat, hubungi BNPB di 117.`;
}

function buildVolcano(v, len) {
  const alert = VOLCANO_ALERTS[v.alertLevel] || VOLCANO_ALERTS[1];
  const near = nearestCity(v.latitude, v.longitude);
  const now = new Date();
  if (len === "short") {
    return `🔺 Gunung ${v.name} (${v.country}) berstatus ${alert.label} (Level ${v.alertLevel}). Elev ${v.elevation} m. Warga waspadai radius KRB. #PANTAU #GunungApi`;
  }
  if (len === "detailed") {
    return `SIARAN PERS — STATUS GUNUNG API ${v.name.toUpperCase()}

Jakarta, ${fmtWIB(now)} — Aktivitas Gunung ${v.name} di ${v.country} saat ini berada pada status ${alert.label.toUpperCase()} (Level ${v.alertLevel}). Gunung bertipe ${v.type} ini memiliki ketinggian ${v.elevation} meter dari permukaan laut, berlokasi pada koordinat ${v.latitude.toFixed(3)}, ${v.longitude.toFixed(3)}${near ? `, sekitar ${near.distanceKm.toFixed(0)} km dari ${near.name}` : ""}.

IMBAUAN
Masyarakat di dalam Kawasan Rawan Bencana (KRB) dan pengunjung diimbau tidak memasuki radius bahaya yang ditetapkan PVMBG. Gunakan masker N95 apabila terdampak abu vulkanik, tutup ventilasi rumah, dan simpan cadangan air bersih. Ikuti rekomendasi resmi Pos Pengamatan Gunung Api setempat dan BNPB.

KONTAK DARURAT
BNPB 117 · BASARNAS 115 · Damkar 113 · Ambulans 118/119

SUMBER
PVMBG / Magma Indonesia · Real-time feed PANTAU (${now.toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })} WIB)`;
  }
  return `Status Gunung ${v.name}: ${alert.label} (Level ${v.alertLevel})

Jakarta — Gunung ${v.name} di ${v.country} kini berstatus ${alert.label.toUpperCase()} (Level ${v.alertLevel}). Gunung tipe ${v.type} setinggi ${v.elevation} m ini berada pada koordinat ${v.latitude.toFixed(2)}, ${v.longitude.toFixed(2)}${near ? ` (± ${near.distanceKm.toFixed(0)} km dari ${near.name})` : ""}.

Warga di dalam Kawasan Rawan Bencana diimbau menjauhi radius yang ditetapkan PVMBG, menggunakan masker N95 saat abu vulkanik turun, dan mengikuti arahan resmi. Kontak darurat: BNPB 117.`;
}

function buildFire(f, len) {
  const near = nearestCity(f.latitude, f.longitude);
  const risk = (f.frp || 0) > 50 || f.confidence === "high" ? "TINGGI" : (f.frp || 0) >= 20 ? "SEDANG" : "RINGAN";
  const coord = `${f.latitude?.toFixed(3)}, ${f.longitude?.toFixed(3)}`;
  const nearStr = near ? `sekitar ${near.distanceKm.toFixed(0)} km dari ${near.name}` : "";
  if (len === "short") {
    return `🔥 Titik panas ${f.instrument || "FIRMS"} terdeteksi di ${coord}${near ? ` (${near.distanceKm.toFixed(0)} km dari ${near.name})` : ""}. FRP ${f.frp ?? "?"} MW · ${(f.confidence || "").toUpperCase()}. Risiko ${risk}. #PANTAU #Karhutla`;
  }
  if (len === "detailed") {
    return `SIARAN PERS — TITIK PANAS KARHUTLA TERDETEKSI

Jakarta, ${fmtWIB(Date.now())} — Sensor ${f.instrument || "VIIRS/MODIS"} pada satelit ${f.satellite || "NOAA-20/Terra/Aqua"} mendeteksi titik panas di koordinat ${coord}${nearStr ? `, ${nearStr}` : ""}. Data dirilis melalui NASA FIRMS.

PARAMETER
• Fire Radiative Power (FRP): ${f.frp ?? "-"} MW
• Suhu kecerahan: ${f.brightness ?? "-"} K
• Tingkat kepercayaan: ${(f.confidence || "-").toUpperCase()}
• Waktu deteksi: ${f.date} (${f.daynight === "D" ? "Day pass" : "Night pass"})
• Klasifikasi risiko (mengacu Sipongi+ KLHK): ${risk}

IMBAUAN
Bagi masyarakat sekitar, batasi aktivitas luar ruangan saat ISPU meningkat dan gunakan masker N95. Tutup ventilasi rumah untuk mencegah masuknya asap. Laporkan titik api ke Manggala Agni atau Damkar 113. Pembakaran lahan dilarang berdasarkan UU No. 32/2009.

KONTAK DARURAT
BNPB 117 · Damkar 113 · BASARNAS 115

SUMBER
NASA FIRMS (${f.instrument || "VIIRS/MODIS"}) · PANTAU real-time feed`;
  }
  return `Titik Panas Terdeteksi — ${coord}

Jakarta — Sensor ${f.instrument || "VIIRS/MODIS"} NASA FIRMS mendeteksi titik panas pada koordinat ${coord}${nearStr ? ` (${nearStr})` : ""} dengan Fire Radiative Power ${f.frp ?? "-"} MW dan tingkat kepercayaan ${(f.confidence || "-").toUpperCase()}. Suhu kecerahan tercatat ${f.brightness ?? "-"} K pada ${f.daynight === "D" ? "lintasan siang" : "lintasan malam"} (${f.date}).

Klasifikasi risiko: ${risk}. Warga sekitar diimbau membatasi aktivitas luar ruangan, menggunakan masker N95, dan segera melaporkan titik api ke Damkar 113 atau BNPB 117.`;
}

const BUILDERS = { quake: buildQuake, volcano: buildVolcano, fire: buildFire };

export default function PressRelease({ open, onOpenChange, kind, data }) {
  const [tab, setTab] = useState("standard");
  const [copied, setCopied] = useState(false);

  const text = useMemo(() => {
    if (!data || !kind) return "";
    return (BUILDERS[kind] || (() => ""))(data, tab);
  }, [data, kind, tab]);

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Naskah disalin ke clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Gagal menyalin. Coba pilih & salin manual.");
    }
  };

  const title = kind === "quake" ? "Naskah Gempa" : kind === "volcano" ? "Naskah Gunung Api" : "Naskah Karhutla";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-3xl max-h-[92vh] overflow-y-auto border-2 border-slate-700 bg-slate-900 text-slate-100 shadow-2xl shadow-cyan-500/10" data-testid="press-release-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Newspaper className="h-4 w-4 text-cyan-400" /> Press Release Generator · {title}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-950">
            <TabsTrigger value="short" data-testid="pr-short"><Twitter className="mr-1 h-3.5 w-3.5" />Short (X / WA)</TabsTrigger>
            <TabsTrigger value="standard" data-testid="pr-standard"><Newspaper className="mr-1 h-3.5 w-3.5" />Standard</TabsTrigger>
            <TabsTrigger value="detailed" data-testid="pr-detailed"><FileText className="mr-1 h-3.5 w-3.5" />Detailed</TabsTrigger>
          </TabsList>
          <TabsContent value={tab} className="mt-3">
            <textarea
              readOnly
              value={text}
              className="min-h-[320px] w-full resize-y whitespace-pre-wrap rounded-lg border border-slate-800 bg-slate-950/70 p-4 font-mono text-xs leading-relaxed text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-500"
              data-testid="press-release-text"
            />
            <div className="mt-1 flex items-center justify-between text-[10px] text-slate-500">
              <span>{text.length} karakter · {text.split(/\s+/).filter(Boolean).length} kata</span>
              <span>Format: {tab === "short" ? "Twitter/WA ready" : tab === "standard" ? "Copy-paste news release" : "Full press-release template"}</span>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button onClick={copyAll} className="bg-cyan-500 text-slate-950 hover:bg-cyan-400" data-testid="press-release-copy">
            {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
            {copied ? "Tersalin" : "Salin Naskah"}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = `pantau-press-release-${kind}-${Date.now()}.txt`;
              a.click();
              toast.success("Naskah diunduh (.txt)");
            }}
            className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
            data-testid="press-release-download"
          >
            <FileText className="mr-2 h-4 w-4" /> Unduh .txt
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
